import type { SendInfiniteEligibilityRegisterInterestEmailResponse } from '~/services/LuxLoyaltyInfiniteApplicationService';

export type EligibilityRegisterInterestSendRow = {
  id: string;
  registerInterestEmailSent: boolean;
  isRetracted: boolean;
};

export type BulkSendRegisterInterestEmailPlan = {
  ids: Array<string>;
  selectedCount: number;
  alreadySentCount: number;
  retractedCount: number;
};

export function planBulkSendRegisterInterestEmail(
  rows: Array<EligibilityRegisterInterestSendRow>,
): BulkSendRegisterInterestEmailPlan {
  const eligibleRows = rows.filter((row) => !row.registerInterestEmailSent && !row.isRetracted);
  return {
    ids: eligibleRows.map((row) => row.id),
    selectedCount: rows.length,
    alreadySentCount: rows.filter((row) => row.registerInterestEmailSent).length,
    retractedCount: rows.filter((row) => row.isRetracted).length,
  };
}

export function getBulkSendRegisterInterestEmailEmptyMessage(): string {
  return 'Selected users have already been sent the register interest email or have been retracted.';
}

export interface BulkSendRegisterInterestEmailResult {
  requestedCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
}

export function aggregateBulkSendRegisterInterestEmailResults(
  results: Array<PromiseSettledResult<SendInfiniteEligibilityRegisterInterestEmailResponse>>,
): BulkSendRegisterInterestEmailResult {
  return results.reduce<BulkSendRegisterInterestEmailResult>(
    (acc, result) => {
      if (result.status === 'rejected') {
        return { ...acc, failedCount: acc.failedCount + 1 };
      }
      if (result.value.result.outcome === 'sent') {
        return { ...acc, sentCount: acc.sentCount + 1 };
      }
      if (result.value.result.outcome === 'skipped') {
        return { ...acc, skippedCount: acc.skippedCount + 1 };
      }
      return { ...acc, failedCount: acc.failedCount + 1 };
    },
    { requestedCount: results.length, sentCount: 0, skippedCount: 0, failedCount: 0 },
  );
}

export function getBulkSendRegisterInterestEmailResultMessage(result: BulkSendRegisterInterestEmailResult): string {
  const parts = [`${result.sentCount} sent`];
  if (result.skippedCount > 0) {
    parts.push(`${result.skippedCount} skipped`);
  }
  if (result.failedCount > 0) {
    parts.push(`${result.failedCount} failed`);
  }
  const userLabel = result.requestedCount === 1 ? 'eligible user' : 'eligible users';
  return `${result.requestedCount} ${userLabel}: ${parts.join(', ')}.`;
}
